import { newReviewAtom } from "@/shared/atoms";
import { useSetPriorityModal } from "@/shared/atoms/usePriorityModal";
import { useSetResetLedger } from "@/shared/atoms/useResetLedger";
import useBranchId from "@/shared/hooks/useBranchId";
import useUnreadNotificationQuery from "@/shared/queries/internalNotifications/useUnreadNotificationQuery";
import useActiveOrdersSessionsQuery from "@/shared/queries/orderssessions/useActiveOrdersSessionsQuery";
import useVoipCurrentQuery from "@/shared/queries/voip/useVoipCurrentQuery";
import useLatestCouriersLocations from "@/shared/v2/queries/useLatestCouriersLocations";
import { useSetAtom } from "jotai";
import { useEffect, useRef } from "react";
import { toast } from "react-toastify";
import VoipCallToast from "../VoipCallToast";
import { useSetLedger } from "@/shared/atoms/useLedgerAtom";
import useVoipApproveHandlerHook from "@/shared/hooks/useVoipApproveHandlerHook";
import { VoipCallStatus } from "@/shared/utils/enum";
import useLiveOrdersRefetch from "@/shared/v2/hooks/useLiveOrdersRefetch";
import { pusherClient } from "@/shared/socket";
import useIsKioskUser from "@/shared/hooks/useIsKioskUser";

const Pusher = () => {
  const branch = useBranchId();
  const isKioskUser = useIsKioskUser();

  const set = useSetAtom(newReviewAtom);
  const { refetch: refetchSessions } = useActiveOrdersSessionsQuery();
  const { refetch: refetchLocations } = useLatestCouriersLocations();
  const { refetch: refetchUnreadNotification } = useUnreadNotificationQuery();
  const { refetch: refetchVoipCurrent } = useVoipCurrentQuery();
  const setResetLedger = useSetResetLedger();
  const setPriorityModal = useSetPriorityModal();
  const setLedger = useSetLedger();
  const handleApprove = useVoipApproveHandlerHook();
  const refetchOrders = useLiveOrdersRefetch();

  const setTimeOut = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (branch == -1 || branch === undefined) return;
    refetchUnreadNotification();

    const branchChannel = "branch-" + branch;
    const channel = pusherClient.subscribe(branchChannel);

    channel.bind("newOrder", ({ custom }) => {
      const milliSeconds = custom?.milli_seconds ?? 1000;
      clearTimeout(setTimeOut.current);

      setTimeOut.current = setTimeout(() => {
        refetchOrders();
      }, milliSeconds);
    });
    channel.bind("newReview", () => {
      set(true);
    });
    // A kiosk has no couriers to show
    if (!isKioskUser) {
      channel.bind("newLocation", () => {
        refetchLocations();
      });
    }
    channel.bind("newInternalNotification", () => {
      refetchUnreadNotification();
    });
    channel.bind("voipCall", ({ custom }) => {
      if (custom === VoipCallStatus.CONNECTED) {
        handleApprove();
      } else if (custom === VoipCallStatus.INCOMING) {
        refetchVoipCurrent().then(() => {
          toast(<VoipCallToast />, { closeButton: true, autoClose: 10000 });
        });
      }
    });
    channel.bind("ledgerSuccess", ({ custom }) => {
      const { ledger } = custom;
      setLedger(ledger);
      setPriorityModal("", {});
    });
    channel.bind("ledgerCompleteSuccess", () => {
      setResetLedger(true);
    });

    channel.bind("sessionPaymentSuccess", () => {
      setResetLedger(true);
      refetchSessions();
    });

    channel.bind("cancelPinPayment", () => {
      setPriorityModal("", {});
    });
    channel.bind("cancelledByExternalProvider", ({ custom }) => {
      const { order } = custom;
      setPriorityModal("orderCancelledByExternalProvider", {
        order,
      });
    });
    channel.bind("driverStatusUpdate", () => {
      refetchOrders();
    });

    return () => {
      setTimeOut.current && clearTimeout(setTimeOut.current);
      pusherClient.unsubscribe(branchChannel);
    };
  }, [branch, isKioskUser]);

  return null;
};

export default Pusher;
